import { createContext, useContext, useEffect, useState } from 'react'
import {
  useGetOrganization,
  useUpdateOrganizationStatus,
  queryClient,
  useGetProfile,
  useGetAllOrganizations,
} from '@yuno/dashboard-api-mfe'
import type { UseQueryResult } from 'react-query'
import type { Organization, User } from '@yuno/types'
import { useAccounts } from '../components/layout/account/account.hooks'
import { storage, Item } from '../lib/storage'
import { datadogRum } from '../utils/monitoring'
import { useVerifyPathPermission } from '../hooks/use-verify-path-permission'
import { InactiveSessionContext } from './inactive-session-context'
import { useLogout } from '../components/account-menu/hooks'
import { useOrganization } from '../hooks/use-organization'
import { useModeState } from '../hooks/use-mode-state'
import { useAccount } from '../yuno-dashboard-container-mfe'

export const ContainerContext = createContext(null)

const SEARCH_PARAM = new URLSearchParams(window.location.search)

export const ContainerProvider = ({ children }) => {
  const account = useAccount()
  const propsHook = useContext(InactiveSessionContext)

  const [organizationSelected, setOrganizationSelected] = useState({
    status: {},
  })

  const { statusFetch, rol, setRol, permissions } = useVerifyPathPermission()

  const { isNotAuth } = useLogout()
  const organizations: UseQueryResult<Organization.Organization[], unknown> =
    useGetAllOrganizations()

  const { organization: organizationCodeStaff } = useOrganization(
    organizations.data,
  )

  const {
    data: organization = null,
    isLoading,
    data,
  }: UseQueryResult<Organization.Organization, unknown> = useGetOrganization()

  const updateOrg = useUpdateOrganizationStatus()
  const {
    data: userProfile,
  }: UseQueryResult<User.UserResponsePayload, unknown> = useGetProfile()

  const {
    handleToggleDrawer,
    handleTestingState,
    handleTestingStateStaff,
    toggleEnvironment,
    testingEnvironment,
    openDrawer,
    setTestingEnvironment,
  } = useModeState({
    organization,
  })

  const {
    accounts,
    isLoadingAccount,
    refetch: refetchAccounts,
    error,
    success,
  } = useAccounts(organizationCodeStaff)

  useEffect(() => {
    refetchAccounts()
  }, [testingEnvironment])

  useEffect(() => {
    if (updateOrg?.status === 'success' && !data?.response?.status) {
      queryClient.refetchQueries(['fetch-organizations'])
    }
  }, [updateOrg.status])

  useEffect(() => {
    if (organizations) {
      const organizationSelected = organizations.data?.find(
        (item) => item.code === organizationCodeStaff,
      )
      setOrganizationSelected(organizationSelected)
    }
  }, [organizations])

  useEffect(() => {
    isNotAuth(data)
    if (!organizationCodeStaff) {
      const isTesting = localStorage.getItem('isTesting')
      if (isLoading && isTesting === null) {
        return
      }
      if (data?.response?.status) return
      handleTestingState(isTesting)
      if (organizations.data) {
        refetchAccounts()
      }
    }
  }, [organization, isLoading])

  useEffect(() => {
    if (organizationCodeStaff) {
      const organizationSelected = organizations.data?.find(
        (item) => item.code === organizationCodeStaff,
      )
      handleTestingStateStaff(organizationSelected)
      if (organizations.data && organizationCodeStaff) {
        refetchAccounts()
      }
    }
  }, [organizationCodeStaff, organizationSelected])

  useEffect(() => {
    if (!accounts && SEARCH_PARAM.has('code') && SEARCH_PARAM.has('state')) {
      storage.removeItem(Item.ACCOUNT)
    }

    if (accounts) {
      const accountStored = storage.getItem<Organization.Account>(Item.ACCOUNT)
      let defaultAccount

      if (Array.isArray(accounts)) {
        if (accountStored === null || accountStored === undefined) {
          defaultAccount = accounts.find((account) => account.principal)
        } else {
          defaultAccount = accounts.find(
            (account) => account.name === accountStored.name,
          )
        }
      } else {
        defaultAccount = accounts
      }

      storage.setItem(Item.ACCOUNT, defaultAccount)
    }
  }, [accounts])

  useEffect(() => {
    if (userProfile) {
      datadogRum.setUser({
        email: userProfile.email,
      })

      setRol(userProfile.rol_id)
    }
  }, [userProfile])

  const handleActivation = () => {
    updateOrg.mutate()
  }

  return (
    <ContainerContext.Provider
      value={{
        organization,
        toggleEnvironment,
        testingEnvironment,
        setTestingEnvironment,
        openDrawer,
        handleToggleDrawer,
        handleActivation,
        accounts,
        account,
        isLoadingAccount,
        isLoadingPermissions: statusFetch !== 'success' && rol !== null,
        permissions,
        userProfile,
        organizations,
        error,
        success,
        organizationCodeStaff,
        statusFetch,
        rol,
        ...propsHook,
      }}
    >
      <div
        onMouseMove={propsHook?.handleEvent}
        onClickCapture={propsHook?.handleEvent}
        onKeyDownCapture={propsHook?.handleEvent}
      >
        {children}
      </div>
    </ContainerContext.Provider>
  )
}
